import { useMemo, useState } from 'react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useServices } from '../../hooks/useServices';
import { useGroups } from '../../hooks/useGroups';
import { useAuth } from '../../context/AuthContext';
import { useClaimBottomBar } from '../../context/BottomBarContext';
import { BulkActionBar } from './BulkActionBar';
import { checkboxClass } from '../common/Input';
import { OverallStatus } from './OverallStatus';
import { FilterBar } from './FilterBar';
import { ServiceCard } from './ServiceCard';
import { ServiceRow, ServiceRowHeader } from './ServiceRow';
import { ViewToggle, DashboardView } from './ViewToggle';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { DashboardSkeleton } from '../common/Skeleton';
import { Service, ServiceStatus } from '../../types';

/** Broken things first — that is the only reason to open this page in a hurry. */
const SEVERITY: Record<ServiceStatus, number> = {
  down: 0, degraded: 1, pending: 2, maintenance: 3, up: 4,
};

function bySeverityThenName(a: Service, b: Service) {
  const d = SEVERITY[a.status] - SEVERITY[b.status];
  return d !== 0 ? d : a.name.localeCompare(b.name);
}

export function Dashboard() {

  useDocumentTitle('Dashboard');
  const { data: services, isLoading } = useServices();
  const { data: groups } = useGroups();
  const { hasPermission } = useAuth();
  const location = useLocation();
  const redirectMessage = (location.state as { message?: string } | null)?.message;

  // Multi-select for bulk actions. Not in the URL: a selection is a moment,
  // not a view worth sharing.
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const canBulk = hasPermission('services.edit');
  const toggleSelected = (id: number) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const stopSelecting = () => { setSelecting(false); setSelected(new Set()); };
  // On a phone the bulk toolbar *is* the bottom bar; stand the tab bar down
  // for the duration rather than letting the two overlap.
  useClaimBottomBar(selecting);

  // Filters live in the URL so a filtered board can be shared and survives a
  // refresh — "the degraded services in production" is now a link.
  const [params, setParams] = useSearchParams();
  const search = params.get('q') ?? '';
  const statusFilter = params.get('status') ?? '';
  const tagFilter = params.get('tag') ?? '';
  const view: DashboardView = params.get('view') === 'cards' ? 'cards' : 'rows';

  const setParam = (key: string, value: string) => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    }, { replace: true });
  };

  const clearFilters = () => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      ['q', 'status', 'tag'].forEach(k => next.delete(k));
      return next;
    }, { replace: true });
  };

  const allTags = useMemo(() => {
    if (!services) return [];
    const tagSet = new Set<string>();
    services.forEach(s => s.tags?.forEach(t => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [services]);

  const filtered = useMemo(() => {
    if (!services) return [];
    const q = search.toLowerCase();
    return services
      .filter(s => {
        if (q && !s.name.toLowerCase().includes(q) && !s.url.toLowerCase().includes(q)) return false;
        if (statusFilter && s.status !== statusFilter) return false;
        if (tagFilter && !s.tags?.includes(tagFilter)) return false;
        return true;
      })
      .sort(bySeverityThenName);
  }, [services, search, statusFilter, tagFilter]);

  const grouped = useMemo(() => {
    if (!groups || groups.length === 0) return null;

    const result: { group: typeof groups[0]; services: Service[] }[] = [];
    const ungrouped = new Set(filtered.map(s => s.id));

    for (const group of groups) {
      const groupServices = filtered.filter(s => group.service_ids?.includes(s.id));
      groupServices.forEach(s => ungrouped.delete(s.id));
      if (groupServices.length > 0) result.push({ group, services: groupServices });
    }

    const remaining = filtered.filter(s => ungrouped.has(s.id));
    if (remaining.length > 0) {
      result.push({
        group: { id: 0, name: 'Ungrouped', description: null, sort_order: 999, created_at: '', updated_at: '' },
        services: remaining,
      });
    }

    return result.length > 0 ? result : null;
  }, [filtered, groups]);

  if (isLoading) return <DashboardSkeleton />;

  const hasFilters = Boolean(search || statusFilter || tagFilter);

  // In select mode every row/card gets a checkbox in front of it; the item
  // itself still links through, so a selection never blocks a look.
  const withCheckbox = (s: Service, node: React.ReactNode, className: string) => (
    <div key={s.id} className={`flex items-stretch gap-2 ${className}`}>
      <label className="flex items-center px-1 cursor-pointer">
        <input
          type="checkbox"
          className={checkboxClass}
          checked={selected.has(s.id)}
          onChange={() => toggleSelected(s.id)}
          aria-label={`Select ${s.name}`}
        />
      </label>
      <div className="min-w-0 flex-1">{node}</div>
    </div>
  );

  const renderServices = (list: Service[]) =>
    view === 'cards' ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map(s => selecting ? withCheckbox(s, <ServiceCard service={s} />, '') : <ServiceCard key={s.id} service={s} />)}
      </div>
    ) : (
      <Card className="divide-y divide-gray-200 dark:divide-gray-700 overflow-hidden">
        <ServiceRowHeader />
        {list.map(s => selecting ? withCheckbox(s, <ServiceRow service={s} />, 'pl-2') : <ServiceRow key={s.id} service={s} />)}
      </Card>
    );

  return (
    <div className="space-y-6">
      {redirectMessage && <Alert tone="error">{redirectMessage}</Alert>}

      <OverallStatus services={services || []} />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <FilterBar
          search={search}
          onSearchChange={v => setParam('q', v)}
          statusFilter={statusFilter}
          onStatusFilterChange={v => setParam('status', v)}
          tagFilter={tagFilter}
          onTagFilterChange={v => setParam('tag', v)}
          tags={allTags}
        />
        <div className="flex items-center gap-3">
          <ViewToggle value={view} onChange={v => setParam('view', v === 'rows' ? '' : v)} />
          {canBulk && (services?.length ?? 0) > 0 && (
            <Button variant={selecting ? 'secondary' : 'ghost'} onClick={() => (selecting ? stopSelecting() : setSelecting(true))} aria-pressed={selecting}>
              {selecting ? 'Cancel' : 'Select'}
            </Button>
          )}
          <Link to="/services/new" className="shrink-0 flex-1 sm:flex-none">
            <Button className="w-full">Add service</Button>
          </Link>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">
            {services?.length === 0 ? 'No services configured yet.' : 'No services match your filters.'}
          </p>
          {services?.length === 0 ? (
            <Link to="/services/new" className="inline-block mt-4">
              <Button>Add your first service</Button>
            </Link>
          ) : (
            <Button variant="secondary" className="mt-4" onClick={clearFilters}>Clear filters</Button>
          )}
        </div>
      ) : grouped ? (
        <div className="space-y-6">
          {grouped.map(({ group, services: groupServices }) => (
            <section key={group.id} aria-label={group.name}>
              <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                {group.name}
                {group.description && (
                  <span className="ml-2 font-normal normal-case tracking-normal">{group.description}</span>
                )}
              </h2>
              {renderServices(groupServices)}
            </section>
          ))}
        </div>
      ) : (
        renderServices(filtered)
      )}

      {hasFilters && filtered.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
          Showing {filtered.length} of {services?.length ?? 0} services.{' '}
          <button onClick={clearFilters} className="underline hover:no-underline">Clear filters</button>
        </p>
      )}

      {selecting && (
        <BulkActionBar
          selected={[...selected].filter(id => filtered.some(s => s.id === id))}
          total={filtered.length}
          groups={groups ?? []}
          onSelectAll={() => setSelected(new Set(filtered.map(s => s.id)))}
          onClear={() => setSelected(new Set())}
          onDone={stopSelecting}
        />
      )}
    </div>
  );
}
